import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DriverPlannings } from "./driverPlanningsApi";
import * as XLSX from "xlsx";
import Ajv from "ajv";
import { authAxios, QueryStatus } from "../../../../utils";
import { RootState } from "../../../../redux/store";
import { getStartTimeAndEndTimeFromExcel } from "./lib";

export type DriverPlanningsState = {
  driverPlannings: DriverPlannings[];
  driverPlanningsStatus: QueryStatus;
  uploadProgress: number;
};

const initialState: DriverPlanningsState = {
  driverPlannings: [],
  driverPlanningsStatus: "idle",
  uploadProgress: 0,
};

const schema = {
  type: "array",
  items: {
    type: "object",
    properties: {
      "Code salarié": {
        type: "string",
      },
      "Date début intervention": {
        type: "string",
        pattern: "^[0-9][0-9]?/[0-9][0-9]?/[0-9]{2,4}$",
      },
      "Heure début intervention": {
        type: "string",
        pattern: "^[0-9]{4}$",
      },
      "Heure fin intervention": {
        type: "string",
        pattern: "^[0-9]{4}$",
      },
      Kilomètres: {
        type: "string",
        pattern: "^[0-9]+\\.?[0-9]*$",
      },
      Commune: {
        type: "string",
      },
      "Nom usuel du membre": {
        type: "string",
      },
      Prénom: {
        type: "string",
      },
      Commune_1: {
        type: "string",
      },
      "Nom Rubrique": {
        type: "string",
      },
    },
    required: [
      "Code salarié",
      "Date début intervention",
      "Heure début intervention",
      "Heure fin intervention",
      "Kilomètres",
    ],
  },
};

type InputLine = {
  "Code salarié": string;
  "Date début intervention": string;
  "Heure fin intervention": string;
  "Heure début intervention": string;
  Kilomètres: string;
  Commune?: string;
  "Nom usuel du membre"?: string;
  Prénom?: string;
  Commune_1?: string;
  "Nom Rubrique"?: string;
};

const ajv = new Ajv({ formats: { date: "date" } });

const validate = ajv.compile(schema);

const parseLine = (line: InputLine): DriverPlannings => {
  const dateSplitted = line["Date début intervention"].split("/");
  let day: string;
  let month: string;
  let year: string;

  if (dateSplitted[2].length === 2) {
    [month, day, year] = dateSplitted;
    year = `20${year}`;
  } else {
    [day, month, year] = dateSplitted;
  }

  const { startTime, endTime } = getStartTimeAndEndTimeFromExcel(
    year,
    month,
    day,
    {
      hours: parseInt(line["Heure début intervention"].slice(0, 2)),
      minutes: parseInt(line["Heure début intervention"].slice(2, 4)),
    },
    {
      hours: parseInt(line["Heure fin intervention"].slice(0, 2)),
      minutes: parseInt(line["Heure fin intervention"].slice(2, 4)),
    },
  );
  const hasKmClient = Boolean(
    line["Nom Rubrique"] &&
      line["Nom Rubrique"].replace(/[^a-zA-Z0-9]/g, "").toLowerCase() ===
        "kmsclient",
  );

  return {
    driverIdNumber: line["Code salarié"],
    expectedMileage: parseFloat(line["Kilomètres"]),
    startTime,
    endTime,
    driverCity: line["Commune"] ?? null,
    clientName:
      line["Nom usuel du membre"] && line["Prénom"]
        ? `${line["Prénom"]} ${line["Nom usuel du membre"]}`
        : null,
    clientCity: line["Commune_1"] ?? null,
    hasKmClient,
    activityType: line["Nom Rubrique"] ?? null,
  };
};

export const processFile = (file: File): Promise<DriverPlannings[]> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      if (event?.target) {
        try {
          const bufferArray = event.target.result;
          const workBook = XLSX.read(bufferArray, { type: "buffer" });
          const sheet = workBook.Sheets[workBook.SheetNames[0]];
          const jsonData: InputLine[] = XLSX.utils.sheet_to_json(sheet, {
            raw: false,
          });
          if (!validate(jsonData)) {
            throw new Error("Invalid data format");
          }
          const data = jsonData.map(parseLine);
          resolve(data);
        } catch (e) {
          reject(e);
        }
      }
    };
  });
export const uploadDriverPlanningsFileThunk = createAsyncThunk(
  "driverPlannings/upload",
  async (payload: { file: File; companyUuid: string }, thunkAPI) => {
    const driverPlannings = await processFile(payload.file);
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });

    //gestion des gros fichiers de données
    const chunkSize = 1000;
    const totalChunks = Math.ceil(driverPlannings.length / chunkSize);

    for (let i = 0; i < driverPlannings.length; i += chunkSize) {
      const chunk = driverPlannings.slice(i, i + chunkSize);
      await axios.post("driver_plannings", {
        driverPlannings: chunk,
        companyUuid: payload.companyUuid,
      });

      // Mettre à jour la progression
      const currentChunk = Math.floor(i / chunkSize) + 1;
      const progress = Math.round((currentChunk / totalChunks) * 100);
      thunkAPI.dispatch(
        driverPlanningsSlice.actions.setUploadProgress(progress),
      );
    }
  },
);

export const driverPlanningsSlice = createSlice({
  name: "driverPlannings",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetPlanningUploadStatus(state) {
      state.driverPlanningsStatus = "idle";
      state.uploadProgress = 0;
    },
    setUploadProgress(state, action: { payload: number }) {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDriverPlanningsFileThunk.pending, (state) => {
        state.driverPlanningsStatus = "processing";
      })
      .addCase(uploadDriverPlanningsFileThunk.fulfilled, (state) => {
        state.driverPlanningsStatus = "success";
      })
      .addCase(uploadDriverPlanningsFileThunk.rejected, (state) => {
        state.driverPlanningsStatus = "failed";
      });
  },
});

export const selectDriverPlanningsStatus = (state: RootState) =>
  state.driverPlannings.driverPlanningsStatus;

export const selectDriverPlanningsUploadProgress = (state: RootState) =>
  state.driverPlannings.uploadProgress;

export default driverPlanningsSlice.reducer;
